'use client'

import { useEffect, useRef } from 'react'
import Link from 'next/link'

import { cn } from '@/core/utils/common'

import type { Route } from 'next'

export type InfoNavKey =
  'faq' | 'delivery' | 'inquiry' | 'guide' | 'brand' | 'terms' | 'privacy'

type InfoNavItem = { key: InfoNavKey; href: string; label: string }

const INFO_GROUPS: { title: string; items: InfoNavItem[] }[] = [
  {
    title: '고객지원',
    items: [
      { key: 'faq', href: '/support', label: '자주 묻는 질문' },
      { key: 'delivery', href: '/support/delivery', label: '배송·교환 안내' },
      { key: 'inquiry', href: '/support/inquiry', label: '1:1 문의' },
    ],
  },
  {
    title: '이용 안내',
    items: [{ key: 'guide', href: '/guide', label: '설치·사용 가이드' }],
  },
  {
    title: '요모컴퍼니',
    items: [{ key: 'brand', href: '/brand', label: '브랜드스토리' }],
  },
  {
    title: '약관',
    items: [
      { key: 'terms', href: '/policy', label: '이용약관' },
      { key: 'privacy', href: '/policy/privacy', label: '개인정보처리방침' },
    ],
  },
]

export function InfoSideNav({ current }: { current: InfoNavKey }) {
  return (
    <nav
      aria-label="고객지원 메뉴"
      className="sticky top-[160px] hidden flex-col gap-7 self-start over10:flex"
    >
      {INFO_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="type-meta font-semibold text-description">
            {group.title}
          </p>
          <ul className="mt-2 flex flex-col">
            {group.items.map((item) => (
              <li key={item.key}>
                <Link
                  href={item.href as Route}
                  aria-current={item.key === current ? 'page' : undefined}
                  className={cn(
                    'type-ui block py-1.5 hover:underline',
                    item.key === current
                      ? 'font-semibold text-point'
                      : 'text-gray'
                  )}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  )
}

export function InfoTabNav({ current }: { current: InfoNavKey }) {
  const stripRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const strip = stripRef.current
    const active = strip?.querySelector<HTMLElement>('[aria-current="page"]')
    if (strip && active) strip.scrollLeft = active.offsetLeft - 20
  }, [])

  return (
    <nav
      aria-label="고객지원 메뉴"
      className="sticky top-16 z-20 -mx-5 mb-6 border-b border-border bg-background over10:hidden"
    >
      <div
        ref={stripRef}
        className="scrollbar-none relative flex gap-5 overflow-x-auto px-5"
      >
        {INFO_GROUPS.flatMap((group) => group.items).map((item) => (
          <Link
            key={item.key}
            href={item.href as Route}
            aria-current={item.key === current ? 'page' : undefined}
            className={cn(
              'type-ui -mb-px shrink-0 border-b-2 py-3.5',
              item.key === current
                ? 'border-foreground font-semibold text-foreground'
                : 'border-transparent text-description'
            )}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </nav>
  )
}
